/**
 * One Lucide glyph, rendered like the prototype's `icon(name)`:
 * `<svg class="icon" width="20" height="20" viewBox="0 0 24 24" …stroke="currentColor"…>`. Unknown names
 * draw 'folder'. A name outside the eager chrome subset draws an empty box of the same size until the
 * full set has loaded (no layout shift).
 */

import { createElement, useEffect, useSyncExternalStore, type SVGProps } from 'react';
import { iconData, iconsVersion, loadAllIcons, resolveIcon, subscribeIcons } from './registry.js';

const camel = (attribute: string): string => attribute.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: string | null | undefined;
  size?: number;
  /** A native tooltip (`<title>`), for icons that stand alone for a name (FolderIcon). */
  title?: string;
}

export function Icon({ name, size = 20, className, title, ...rest }: IconProps) {
  useSyncExternalStore(subscribeIcons, iconsVersion, iconsVersion);
  const resolved = resolveIcon(name);
  useEffect(() => {
    if (resolved === null) void loadAllIcons().catch(() => undefined);
  }, [resolved]);
  const data = resolved === null ? null : iconData(resolved);
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {data?.nodes.map(([tag, attributes], i) =>
        createElement(tag, { key: i, ...Object.fromEntries(Object.entries(attributes).map(([k, v]) => [camel(k), v])) }),
      )}
    </svg>
  );
}
